<template>
  <div class="space-y-4">
    <!-- ICD Code Input Section -->
    <div class="bg-white rounded-lg border border-gray-200 p-4">
      <h3 class="font-semibold text-gray-900 mb-3">Add ICD Code</h3>

      <!-- Code Input -->
      <div class="flex items-center gap-2">
        <div class="flex-1">
          <label class="block text-sm font-medium text-gray-700 mb-1">
            ICD Code:
          </label>
          <div class="flex items-center gap-2">
            <input
              v-model="icdSearchCode"
              type="text"
              placeholder="Enter ICD code"
              class="px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-dark focus:border-transparent"
              :disabled="isLoading || loadingBillingContext"
            />
            <button
              @click="checkHCC"
              :disabled="isLoading || !icdSearchCode || loadingBillingContext"
              :class="[
                'px-2 py-1 rounded-md text-sm transition-colors flex items-center gap-2',
                isLoading || !icdSearchCode || loadingBillingContext
                  ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                  : 'bg-primary-500 text-white hover:bg-primary-600'
              ]"
            >
              <FontAwesomeIcon v-if="hccLoading" :icon="['fas', 'spinner']" class="animate-spin" />
              <span v-else>Check HCC</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Validation Errors -->
      <div v-if="icdErrors.length > 0" class="mt-2">
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <ul class="text-sm text-red-600 list-disc list-inside">
            <li v-for="(error, index) in icdErrors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>

      <!-- ICD Code Information -->
      <div v-if="icdSearchResponse.short_description" class="mt-3">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p class="text-sm text-blue-900 font-medium">{{ icdSearchResponse.short_description }}</p>
        </div>
      </div>

      <!-- HCC Mappings -->
      <div v-if="icdSearchResponse.model_hcc_array && icdSearchResponse.model_hcc_array.length > 0" class="mt-3">
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-sm font-medium text-gray-700">HCC Codes:</span>
          <div v-for="value in icdSearchResponse.model_hcc_array" :key="`${value.model_num}-${value.hcc_code}`"
               class="inline-flex items-center gap-1">
            <span class="text-sm font-semibold text-gray-700">v{{ value.model_num }}:</span>
            <span class="px-2 py-1 bg-primary-100 text-primary-700 rounded-md text-sm border border-primary-300">
              {{ value.hcc_code }}
            </span>
          </div>
        </div>
      </div>

      <!-- Last Billed Info (Suspect workflow) -->
      <div v-if="icdLastBilled" class="mt-3">
        <div class="bg-purple-50 border border-purple-200 rounded-lg p-3">
          <p class="text-sm font-medium text-purple-900 mb-1">Last Billed:</p>
          <p class="text-sm text-purple-700">
            {{ icdLastBilled.clm_thru_dt }} by <span class="sensitive">{{ icdLastBilled.provider_name }}</span>
            <span v-if="icdLastBilled.billed_by_practice" class="font-medium">(Patient PCP)</span>
          </p>
        </div>
      </div>

      <!-- Documentation Status -->
      <div v-if="eligibleIcdHccCode" class="mt-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ workflowType === 'encounter' ? 'Documentation Status' : 'Condition Category' }}:
          <span class="text-red-500">*</span>
        </label>
        <select
          v-model="icdSuggestedCodeStatus"
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          :disabled="isLoading"
        >
          <option value="">-- Select Status --</option>
          <option v-for="option in statusOptions" :key="option" :value="option">
            {{ option }}
          </option>
        </select>
      </div>

      <!-- Explanation -->
      <div v-if="eligibleIcdHccCode" class="mt-4">
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Clinical Evidence:
          <span v-if="requiresExplanation" class="text-red-500">*</span>
        </label>
        <textarea
          v-model="icdSearchCodeExplanation"
          rows="4"
          placeholder="Enter clinical evidence for the code..."
          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-dark focus:border-transparent"
          :disabled="isLoading"
        ></textarea>
      </div>

      <!-- Explanation Errors -->
      <div v-if="explanationErrors.length > 0" class="mt-2">
        <div class="bg-red-50 border border-red-200 rounded-lg p-3">
          <ul class="text-sm text-red-600 list-disc list-inside">
            <li v-for="(error, index) in explanationErrors" :key="index">{{ error }}</li>
          </ul>
        </div>
      </div>

      <!-- Ready to Submit Indicator -->
      <div v-if="eligibleIcdHccCode && icdSuggestedCodeStatus && isValidExplanation" class="mt-4">
        <div class="bg-green-50 border border-green-200 rounded-lg p-3">
          <p class="text-sm text-green-700 flex items-center">
            <FontAwesomeIcon :icon="['fas', 'check-circle']" class="mr-2" />
            Code {{ icdSearchCode }} is ready to submit. Click "Submit Work" below to save.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'CodeSuggestionWorkForm',
  components: {
    FontAwesomeIcon
  },
  props: {
    workType: {
      type: Object,
      required: true
    },
    taskId: {
      type: Number,
      default: null
    },
    taskTypeId: {
      type: Number,
      required: true
    },
    taskMetadata: {
      type: Object,
      required: true
    },
    opportunities: {
      type: Array,
      required: true
    },
    currentUser: {
      type: Object,
      default: null
    }
  },
  emits: ['validation-change'],
  data() {
    return {
      // Billing context
      loadingBillingContext: true,
      dxCodesBilled: [],
      currentHCCs: {
        24: [],
        28: []
      },
      recaptureHCCs: {
        24: [],
        28: []
      },

      // Existing work items
      existingWorkItems: [],

      // Form fields
      icdSearchCode: '',
      icdSearchResponse: {},
      icdSearchCodeExplanation: '',
      icdSuggestedCodeStatus: '',
      eligibleIcdHccCode: false,
      icdLastBilled: null,

      // Loading states
      hccLoading: false,
      isLoading: false,

      // Errors
      icdErrors: [],
      explanationErrors: []
    };
  },
  computed: {
    workflowType() {
      if (!this.taskTypeId) return 'encounter';

      const encounterTaskTypes = [6, 7, 8, 9];
      const suspectTaskTypes = [1, 2, 3, 4];
      const earlyStartTaskTypes = [10, 11, 12, 13];

      if (encounterTaskTypes.includes(this.taskTypeId)) {
        return 'encounter';
      } else if (suspectTaskTypes.includes(this.taskTypeId)) {
        return 'suspect';
      } else if (earlyStartTaskTypes.includes(this.taskTypeId)) {
        return 'earlystart';
      }
      return 'encounter';
    },

    statusOptions() {
      if (this.workflowType === 'encounter') {
        return [
          'Meat, Present',
          'Meat, Missing Status and Plan',
          'Meat, Missing Status',
          'Meat, Missing Plan'
        ];
      } else if (this.workflowType === 'suspect') {
        return ['Suspect Condition', 'Recapture Diagnosis'];
      } else if (this.workflowType === 'earlystart') {
        return ['Suspected Condition', 'Active Recapture', 'Confirmed Recapture'];
      }
      return [];
    },

    requiresExplanation() {
      if (this.workflowType === 'encounter') {
        return this.icdSuggestedCodeStatus !== 'Meat, Present';
      }
      return true;
    },

    isValidExplanation() {
      if (!this.icdSuggestedCodeStatus) return false;
      if (!this.requiresExplanation) return true;
      return this.icdSearchCodeExplanation.trim().length > 0;
    },

    isValid() {
      if (!this.eligibleIcdHccCode) {
        return false;
      }

      if (!this.icdSuggestedCodeStatus) {
        return false;
      }

      if (!this.isValidExplanation) {
        return false;
      }

      if (this.icdErrors.length > 0) {
        return false;
      }

      return true;
    }
  },
  watch: {
    icdSearchCode(newVal) {
      if (newVal !== newVal.toUpperCase()) {
        this.icdSearchCode = newVal.toUpperCase();
        return;
      }

      const alphanumericRegex = /^[a-zA-Z0-9]*$/;
      if (!alphanumericRegex.test(newVal)) {
        this.icdErrors = ['Only numbers and letters are allowed'];
      } else {
        this.icdErrors = [];
      }

      this.icdSearchResponse = {};
      this.icdLastBilled = null;
      this.eligibleIcdHccCode = false;
      this.icdSuggestedCodeStatus = '';
      this.icdSearchCodeExplanation = '';
      this.$emit('validation-change', false);
    },

    icdSuggestedCodeStatus() {
      this.explanationErrors = [];
      this.$emit('validation-change', this.isValid);
    },

    icdSearchCodeExplanation() {
      this.explanationErrors = [];
      this.$emit('validation-change', this.isValid);
    },

    eligibleIcdHccCode() {
      this.$emit('validation-change', this.isValid);
    }
  },
  async mounted() {
    await Promise.all([
      this.fetchBillingContext(),
      this.fetchExistingWorkItems()
    ]);
    this.$emit('validation-change', false);
  },
  methods: {
    /**
     * Get list of forbidden ICD codes based on workflow type
     */
    getForbiddenIcds() {
      const forbidden = [];

      // Encounter only: ICDs already on this specific claim
      if (this.workflowType === 'encounter') {
        forbidden.push(...this.dxCodesBilled);
      }

      return [...new Set(forbidden)]; // Remove duplicates
    },

    /**
     * Get list of forbidden HCC codes based on workflow type
     * Returns array of strings like "28-19", "24-85"
     */
    getForbiddenHccs() {
      const forbidden = [];

      // All workflows: HCCs billed in current performance year
      // These codes are COMPLETELY BLOCKED (already captured this year)
      Object.keys(this.currentHCCs).forEach(modelNum => {
        this.currentHCCs[modelNum].forEach(hcc => {
          forbidden.push(`${modelNum}-${hcc.code}`);
        });
      });

      // NOTE: We do NOT include recaptureHCCs in forbidden list
      // Codes with billing history from last year should be allowed as Recapture
      // The status dropdown will enforce that these cannot be marked as "Suspect"

      return [...new Set(forbidden)]; // Remove duplicates
    },

    /**
     * Build complete forbidden lists
     */
    buildForbiddenLists() {
      return {
        forbiddenIcds: this.getForbiddenIcds(),
        forbiddenHccs: this.getForbiddenHccs()
      };
    },

    /**
     * Validate an ICD code against forbidden lists
     */
    validateIcdCode(icdCode, hccArray) {
      const { forbiddenIcds, forbiddenHccs } = this.buildForbiddenLists();

      // Check if ICD itself is forbidden
      if (forbiddenIcds.includes(icdCode)) {
        if (this.workflowType === 'encounter' && this.dxCodesBilled.includes(icdCode)) {
          return {
            valid: false,
            reason: 'ICD code already billed in this encounter'
          };
        }
        return {
          valid: false,
          reason: 'This ICD code has already been suggested'
        };
      }

      // Check if ALL HCC mappings are forbidden (must be blocked in all model versions)
      if (hccArray && Array.isArray(hccArray) && hccArray.length > 0) {
        const forbiddenHccMappings = [];
        const allowedHccMappings = [];

        for (const hcc of hccArray) {
          const hccKey = `${hcc.model_num}-${hcc.hcc_code}`;
          if (forbiddenHccs.includes(hccKey)) {
            forbiddenHccMappings.push(hcc);
          } else {
            allowedHccMappings.push(hcc);
          }
        }

        // Only reject if ALL HCC mappings are forbidden
        // If at least one model version has an unbilled HCC, allow it
        if (forbiddenHccMappings.length === hccArray.length) {
          // All HCC mappings are in currentHCCs - code already captured this performance year
          const currentYear = new Date().getFullYear();
          return {
            valid: false,
            reason: `HCC code already captured in current performance year (${currentYear})`
          };
        }
      }

      return { valid: true };
    },

    async fetchExistingWorkItems() {
      try {
        const opportunityIds = this.opportunities.map(opp => opp.id);
        const response = await axios.get('/api/work/', {
          params: {
            opportunity__in: opportunityIds.join(','),
            work_type: this.workType.id
          }
        });
        this.existingWorkItems = response.data;
      } catch (error) {
        console.error('Error fetching existing work items:', error);
        this.existingWorkItems = [];
      }
    },

    async fetchBillingContext() {
      this.loadingBillingContext = true;

      try {
        if (this.workflowType === 'encounter') {
          await this.fetchEncounterContext();
        } else if (['suspect', 'earlystart'].includes(this.workflowType)) {
          await this.fetchSuspectContext();
        }
      } catch (error) {
        console.error('Error fetching billing context:', error);
      } finally {
        this.loadingBillingContext = false;
      }
    },

    async fetchEncounterContext() {
      if (!this.taskMetadata.patient_identifier || !this.taskMetadata.clm_thru_dt) {
        console.error('Missing patient identifier or claim through date');
        return;
      }

      try {
        const response = await axios.post('/api/returnmetricinsight/', {
          metric_id: 30,
          metric_insight_id: 1,
          insight_type: 'task_insight',
          sql_filters: {
            bene_mbi_id: this.taskMetadata.patient_identifier,
            clm_thru_dt: this.taskMetadata.clm_thru_dt,
            tin: this.taskMetadata.tin
          }
        });

        if (response?.data?.length > 0) {
          const claimData = response.data[0];
          if (claimData.dx_codes_billed) {
            try {
              this.dxCodesBilled = JSON.parse(claimData.dx_codes_billed);
            } catch (e) {
              console.error('Error parsing dx_codes_billed:', e);
              this.dxCodesBilled = [];
            }
          }

          // Also get current HCCs for encounter workflow
          if (claimData.model_hcc_codes) {
            try {
              const hccCodes = JSON.parse(claimData.model_hcc_codes);
              Object.keys(hccCodes).forEach(modelNum => {
                if (!this.currentHCCs[modelNum]) {
                  this.currentHCCs[modelNum] = [];
                }
                hccCodes[modelNum].forEach(code => {
                  this.currentHCCs[modelNum].push({ code });
                });
              });
            } catch (e) {
              console.error('Error parsing model_hcc_codes:', e);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching encounter context:', error);
      }
    },

    async fetchSuspectContext() {
      if (!this.taskMetadata.patient_identifier) {
        console.error('Missing patient identifier');
        return;
      }

      try {
        // Performance year aligns with calendar year for active campaigns
        const performanceYear = new Date().getFullYear();
        const response = await axios.post('/api/returnmetricinsight/', {
          metric_id: 34,
          metric_insight_id: 1,
          insight_type: 'task_insight',
          sql_filters: {
            observation_dt_year: { 'IN': [performanceYear, performanceYear - 1] },
            bene_mbi_id: this.taskMetadata.patient_identifier
          }
        });

        if (response?.data?.length > 0) {
          response.data
            .filter(item => item.model_num !== 22)
            .forEach(item => {
              const { model_num, hcc_code, billed_by_practice, observation_dt_year } = item;
              const hccData = {
                code: hcc_code,
                billed_by_practice,
                clm_thru_dt: item.clm_thru_dt,
                billed_dx: JSON.parse(item.billed_dx),
                npi: item.npi,
                tin: item.tin,
              };

              const targetCollection = observation_dt_year === performanceYear ?
                this.currentHCCs : this.recaptureHCCs;

              const existingIndex = targetCollection[model_num]?.findIndex(hcc => hcc.code === hcc_code);

              if (existingIndex === -1) {
                if (!targetCollection[model_num]) targetCollection[model_num] = [];
                targetCollection[model_num].push(hccData);
              } else if (!targetCollection[model_num][existingIndex].billed_by_practice && billed_by_practice) {
                targetCollection[model_num][existingIndex] = hccData;
              }
            });
        }
      } catch (error) {
        console.error('Error fetching suspect context:', error);
      }
    },

    async checkHCC() {
      this.hccLoading = true;
      this.icdErrors = [];
      this.explanationErrors = [];
      this.eligibleIcdHccCode = false;

      try {
        const response = await axios.post('/api/returnreferencedata/', {
          reference_name: 'DX_TO_HCC_XWALK',
          sql_filters: {
            dx_code: this.icdSearchCode,
          }
        });

        if (response?.data?.length > 0) {
          const data = response.data[0];
          this.icdSearchResponse = {
            ...data,
            model_hcc_array: JSON.parse(data.model_hcc_array)
          };

          const hccArray = this.icdSearchResponse.model_hcc_array;
          const hasValidHccMapping = Array.isArray(hccArray) && hccArray.length > 0;

          if (!hasValidHccMapping) {
            this.icdErrors = ['No HCC mapping found for this ICD code'];
            this.hccLoading = false;
            this.$emit('validation-change', false);
            return;
          }

          // Use unified validation framework
          const validation = this.validateIcdCode(this.icdSearchCode, hccArray);

          if (!validation.valid) {
            this.icdErrors = [validation.reason];
            this.hccLoading = false;
            this.$emit('validation-change', false);
            return;
          }

          // If we get here, the code is eligible
          this.eligibleIcdHccCode = true;

          // Fetch last billed info for suspect/earlystart workflows
          if (['suspect', 'earlystart'].includes(this.workflowType)) {
            await this.fetchLastBilled();
          }
        } else {
          this.icdSearchResponse = {};
          this.icdErrors = ['Could not find HCC code associated with this ICD'];
          this.$emit('validation-change', false);
        }
      } catch (error) {
        console.error('Error fetching HCC data:', error);
        this.icdErrors = ['Failed to check HCC code'];
        this.$emit('validation-change', false);
      } finally {
        this.hccLoading = false;
        this.$emit('validation-change', this.isValid);
      }
    },

    async fetchLastBilled() {
      if (!this.taskMetadata.patient_identifier || !this.icdSearchCode) return;

      try {
        const response = await axios.post('/api/returnmetricinsight/', {
          metric_id: 34,
          metric_insight_id: 2,
          insight_type: 'task_insight',
          sql_filters: {
            bene_mbi_id: this.taskMetadata.patient_identifier,
            dx_code: this.icdSearchCode,
          }
        });

        if (response?.data?.length > 0) {
          this.icdLastBilled = response.data[0];
        }
      } catch (error) {
        console.error('Error fetching last billed:', error);
      }
    },

    getFormData() {
      if (this.isValid) {
        return {
          code: this.icdSearchCode,
          explanation: this.icdSearchCodeExplanation.trim(),
          status: this.icdSuggestedCodeStatus,
          short_description: this.icdSearchResponse.short_description,
          model_hcc_array: this.icdSearchResponse.model_hcc_array,
          icd_last_billed: this.icdLastBilled,
          workflow_type: this.workflowType,
          added_by: this.currentUser ? {
            appuser_id: this.currentUser.id,
            appuser_name: `${this.currentUser.last_name}, ${this.currentUser.first_name}`,
            created_at: new Date().toISOString(),
          } : null
        };
      }
      return {};
    }
  }
};
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
